import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Check, Clock, MapPin, Package, Sparkles, Ticket } from 'lucide-react';
import { api } from '../api/client';
import type { EventItem } from '../api/types';
import { useCart } from '../context/CartContext';

const TAX_RATE = 0.0675;
const DONATION_MAX_TICKETS = 3;

export default function EventDetail() {
  // 1) Top-level hooks
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initial: EventItem | undefined = (location.state as { event?: EventItem } | null)?.event;
  const [event, setEvent] = useState<EventItem | undefined>(initial);
  const [loading, setLoading] = useState(!initial);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addToCart } = useCart();
  const [ticketCount, setTicketCount] = useState(1);
  const [donationAmount, setDonationAmount] = useState('');
  const [donationError, setDonationError] = useState<string | null>(null);
  const [coverSalesTax, setCoverSalesTax] = useState(false);

  // 2) useMemo
  const images = useMemo(() => {
    const urls = event?.imageUrls;
    if (urls && urls.length > 0) return urls;
    return [''];
  }, [event?.imageUrls]);

  const heroImageUrl = useMemo(() => images[0], [images]);

  const displayDate = useMemo(() => {
    if (!event?.startDate) return '';
    const d = new Date(event.startDate);
    if (isNaN(d.getTime())) return event.startDate;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }, [event]);

  const displayPriceText = useMemo(() => {
    const p = event?.price ?? 0;
    return Number.isInteger(p) ? String(p) : p.toFixed(2);
  }, [event]);

  const parsedDonationAmount = useMemo(() => {
    if (!event?.isDonation) return null;
    const trimmed = donationAmount.trim();
    if (!trimmed) return null;
    const num = Number(trimmed);
    if (!Number.isFinite(num)) return null;
    return num;
  }, [donationAmount, event]);

  const donationValid = useMemo(() => {
    if (!event?.isDonation) return true;
    return parsedDonationAmount !== null && parsedDonationAmount > 0;
  }, [event, parsedDonationAmount]);

  const maxTickets = useMemo(() => {
    if (event?.isDonation) return DONATION_MAX_TICKETS;
    return event?.capacity ?? 10;
  }, [event]);

  const unitSubtotal = useMemo(() => {
    if (!event) return 0;
    if (!event?.isDonation) return event.price ?? 0;
    const entered = parsedDonationAmount ?? 0;
    return coverSalesTax ? entered : (entered / (1 + TAX_RATE));
  }, [coverSalesTax, event, parsedDonationAmount]);

  const estimatedSubtotal = useMemo(() => unitSubtotal * ticketCount, [ticketCount, unitSubtotal]);
  const estimatedTax = useMemo(() => estimatedSubtotal * TAX_RATE, [estimatedSubtotal]);
  const estimatedTotal = useMemo(() => estimatedSubtotal + estimatedTax, [estimatedSubtotal, estimatedTax]);
  const estimatedSubtotalText = useMemo(() => estimatedSubtotal.toFixed(2), [estimatedSubtotal]);
  const estimatedTotalText = useMemo(() => estimatedTotal.toFixed(2), [estimatedTotal]);

  const isSoldOut = useMemo(() => {
    return event?.capacity !== undefined && event.capacity < 1;
  }, [event]);

  const addToCartDisabled = useMemo(() => {
    return addedToCart || isSoldOut || !donationValid;
  }, [addedToCart, donationValid, isSoldOut]);

  const addToCartButtonClassName = useMemo(() => {
    const base = 'w-full py-6 rounded-full font-sans text-lg transition-colors duration-150';
    if (addToCartDisabled) return `${base} bg-[#E5E5E5] text-[#B0B0B0] cursor-not-allowed opacity-60`;
    return `${base} bg-[#C9A962] hover:bg-[#B8983F] text-white`;
  }, [addToCartDisabled]);

  // 3) Lifecycle hooks
  useEffect(() => {
    if (initial) return;
    const load = async () => {
      try {
        setLoading(true);
        const events: EventItem[] = await api.events();
        const found = events.find((e) => String(e.squareProductId) === id);
        if (found) setEvent(found);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, initial]);

  // 5) Other variables
  const isWorkshop = event?.eventType === 'workshop';

  // 6) Return (JSX)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#D4A5A5]">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-[#3D3D3D] mb-4">Event not found</h2>
          <Link to="/events" className="inline-block">
            <button className="bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white rounded-full px-6 py-3 font-sans">Back to Events</button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      {/* Hero Image */}
      <div className="relative h-[40vh] sm:h-[50vh] overflow-hidden">
        <img src={heroImageUrl} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#3D3D3D]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            {event.eventType && (
              <span className={`inline-block px-4 py-2 rounded-full font-sans text-sm font-medium mb-4 ${isWorkshop ? 'bg-[#C9A962] text-white' : 'bg-[#9CAF88] text-white'}`}>
                {isWorkshop ? 'Workshop' : 'Market Day'}
              </span>
            )}
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-white">{event.title}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/events" className="inline-flex items-center gap-2 text-[#3D3D3D]/70 hover:text-[#D4A5A5] font-sans mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Events
        </Link>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Event Details */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-2">
            {/* Quick Info Cards */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <Calendar className="w-6 h-6 text-[#D4A5A5] mb-3" />
                <p className="font-sans text-sm text-[#3D3D3D]/60 mb-1">Date</p>
                <p className="font-serif text-lg text-[#3D3D3D]">{displayDate}</p>
              </div>
              {event.startTime && (
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <Clock className="w-6 h-6 text-[#D4A5A5] mb-3" />
                  <p className="font-sans text-sm text-[#3D3D3D]/60 mb-1">Time</p>
                  <p className="font-serif text-lg text-[#3D3D3D]">{event.startTime}{event.endTime ? ` - ${event.endTime}` : ''}</p>
                </div>
              )}
              {event.location && (
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <MapPin className="w-6 h-6 text-[#D4A5A5] mb-3" />
                  <p className="font-sans text-sm text-[#3D3D3D]/60 mb-1">Location</p>
                  <p className="font-serif text-lg text-[#3D3D3D]">{event.location}</p>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div className="bg-white rounded-2xl p-8 shadow-sm mb-8">
                <h2 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-4">About This Event</h2>
                <div className="font-sans text-[#3D3D3D]/70 leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: event.description }} />
              </div>
            )}

            {/* Workshop Details */}
            {event.eventType === 'workshop' && (false) && (
              <div className="grid sm:grid-cols-2 gap-6">
                {false && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <Package className="w-6 h-6 text-[#9CAF88]" />
                      <h3 className="font-serif text-xl font-semibold text-[#3D3D3D]">What's Included</h3>
                    </div>
                    <div className="font-sans text-[#3D3D3D]/70 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: 'event.whatsIncluded' }} />
                  </div>
                )}
                {false && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <Sparkles className="w-6 h-6 text-[#C9A962]" />
                      <h3 className="font-serif text-xl font-semibold text-[#3D3D3D]">What to Bring</h3>
                    </div>
                    <div className="font-sans text-[#3D3D3D]/70 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: 'event.whatToBring' }} />
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Ticket Purchase */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="bg-white rounded-3xl p-8 shadow-lg sticky top-32">
              {isWorkshop ? (
                <>
                  <div className="mb-6">
                    <div>
                      <p className="font-sans text-sm text-[#3D3D3D]/60">{event.isDonation ? 'Choose Your Price' : 'Ticket Price'}</p>
                      {event.isDonation ? (
                        <div className="mt-2">
                          <input
                            aria-label="Choose Your Price"
                            type="number"
                            inputMode="decimal"
                            min={0.01}
                            step={0.01}
                            value={donationAmount}
                            onChange={handleDonationAmountChange}
                            className="px-4 py-6 rounded-xl border-gray-200 font-sans w-full"
                            placeholder="0.00"
                          />
                          <label className="mt-3 flex items-center gap-3 font-sans text-sm text-[#3D3D3D]">
                            <input
                              type="checkbox"
                              checked={coverSalesTax}
                              onChange={handleCoverSalesTaxChange}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            Cover sales tax (6.75%)
                          </label>
                          {donationError && (
                            <p className="font-sans text-sm text-[#D4A5A5] mt-2">{donationError}</p>
                          )}
                        </div>
                      ) : (
                        <p className="font-serif text-3xl font-semibold text-[#3D3D3D]">${displayPriceText}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="row">
                      <label htmlFor={`ticket-count-${event.squareProductId}`} className="font-sans text-sm text-[#3D3D3D] mb-2 block">Number of Tickets{event.isDonation ? ' (Max 3 per transaction)' : ''}</label>
                      <input
                        id={`ticket-count-${event.squareProductId}`}
                        title={`ticket-count-${event.squareProductId}`}
                        type="number"
                        min={1}
                        max={maxTickets}
                        value={ticketCount}
                        onChange={handleTicketCountChange}
                        className="px-4 py-6 rounded-xl border-gray-200 font-sans col-8"
                      />
                      {event.capacity !== undefined && (
                        <div className="text-right col-4">
                          <p className="font-sans text-sm text-[#3D3D3D]/60">Spots Left</p>
                          <p className="font-serif text-2xl font-semibold text-[#D4A5A5]">{event.capacity}</p>
                        </div>
                      )}
                    </div>
                    <div className="bg-[#FDF8F5] rounded-xl p-4">
                      <div className="flex justify-between items-center">
                        <span className="font-sans text-[#3D3D3D]">Estimated total (incl. tax)</span>
                        <span className="font-serif text-2xl font-semibold text-[#3D3D3D]">${estimatedTotalText}</span>
                      </div>
                      {event.isDonation && donationValid && (
                        <div className="mt-2 flex justify-between items-center">
                          <span className="font-sans text-sm text-[#3D3D3D]/60">Subtotal / donation</span>
                          <span className="font-sans text-sm text-[#3D3D3D]/60">${estimatedSubtotalText}</span>
                        </div>
                      )}
                    </div>
                    <button onClick={handleAddToCart} disabled={addToCartDisabled} className={addToCartButtonClassName}>
                      {addedToCart ? (
                        <>
                          <Check className="w-5 h-5 mr-2 inline" /> Added to Cart
                        </>
                      ) : isSoldOut ? (
                        'Sold Out'
                      ) : (
                        <>
                          <Ticket className="w-5 h-5 mr-2 inline" /> Add to Cart
                        </>
                      )}
                    </button>
                    {addedToCart && (
                      <button disabled={event.isDonation && !donationAmount} onClick={handleViewCart} className="w-full border border-[#D4A5A5] text-[#D4A5A5] hover:bg-[#D4A5A5]/10 py-6 rounded-full font-sans">View Cart</button>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <Calendar className="w-12 h-12 text-[#9CAF88] mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-[#3D3D3D] mb-2">Free Event</h3>
                  <p className="font-sans text-[#3D3D3D]/70 mb-6">No tickets needed! Just come by and say hello.</p>
                  <div className="bg-[#9CAF88]/20 rounded-xl p-4">
                    <p className="font-sans text-sm text-[#9CAF88] font-medium">Add this to your calendar!</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );

  function handleDonationAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDonationAmount(e.target.value);
    if (donationError) setDonationError(null);
  }

  function handleCoverSalesTaxChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCoverSalesTax(e.target.checked);
  }

  function handleTicketCountChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = Math.max(1, parseInt(e.target.value) || 1);
    setTicketCount(Math.min(next, maxTickets));
  }

  function handleViewCart() {
    navigate('/cart');
  }

  function handleAddToCart() {
    if (!event) return;
    if (event.isDonation && !donationValid) {
      setDonationError('Please enter an amount greater than $0.00');
      return;
    }

    addToCart({
      id: String(event.squareProductId),
      type: 'event',
      title: event.title,
      // Price is the pre-tax unit price; checkout will compute tax.
      price: unitSubtotal,
      imageUrl: heroImageUrl,
      locationId: event.locationId,
      // Event metadata for cart display
      date: event.startDate,
      time: event.startTime,
      location: event.location,
      quantity: ticketCount,
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  }
}
